// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomErrorClass, ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { Color, SolidBrush } from '@shared/commons/Graphics';
import { LinkedSystem } from '../misc/LinkedSystem';
import MarketDepthOETemplate from '@front/templates/MarketDepthOETemplate.html';
import { Level2ItemQ, MDLotsMode } from '../cache/Level2ItemQ';
import { QuickTableRactive } from '../elements/QuickTable/QuickTableRactive';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { OrderEditViewBase } from './OrderEditViewBase';
import { ThemeManager } from '../misc/ThemeManager';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { Leve2QuoteType } from '@shared/utils/Enums/Constants';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { DynProperty } from '@shared/commons/DynProperty';
import { Instrument } from '@shared/commons/cache/Instrument';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { OrderEditUpdateData } from '@shared/utils/Trading/OrderEditUpdateData';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { TradingNumericErrorChecker } from '@shared/commons/Trading/TradingNumericErrorChecker';
import { PanelItemsFactory } from './PanelItemsFactory';
import { QuickTableUtils } from '../elements/QuickTable/QuickTableUtils';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { WDSettings } from '../settings/WDGeneralSettings';
import { Level1ItemType } from '@shared/utils/Enums/Level1ItemType';
import { HotkeysManager } from '@shared/commons/cache/Hotkeys/HotkeysManager';
import { MarketDepthHotkeysEnum, OrderCancelActionHotkeysEnum } from '@shared/commons/cache/Hotkeys/NamesEventHotkey ';
import { LimitOrderType } from '@shared/commons/cache/OrderParams/order-type/LimitOrderType';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
const MARKET_DEPTH_ROW_HEIGHT = 24;
const BOOLEAN_PROPERTIES = [
    'mirror',
    'visibilityLvl1',
    'visibilityOE',
    'visibilityPos',
    'visibilityProductType',
    'visibilityLeverage',
    'visibilityDataSource',
    'DataSourceOpened'
];
export class MarketDepthPanel extends OrderEditViewBase {
    constructor() {
        super(...arguments);
        this.quickTableRactiveBID = null;
        this.quickTableRactiveASK = null;
        this.headerLocaleKey = 'panel.level2';
        this.ColoringMethod = ColoringMethod.SizeHistogram;
    }
    getType() {
        return PanelNames.MarketDepthPanel;
    }
    updatePanelHeader() {
        const ins = this.get('instrument');
        void this.set({
            header: Resources.getResource(this.headerLocaleKey) + (!isNullOrUndefined(ins) ? ' ' + ins.DisplayName() : '')
        });
    }
    layoutTable() {
        const tableBID = this.quickTableRactiveBID;
        const tableASK = this.quickTableRactiveASK;
        if (!isNullOrUndefined(tableBID)) {
            void this.layoutTableResize(tableBID);
        }
        if (!isNullOrUndefined(tableASK)) {
            void this.layoutTableResize(tableASK);
        }
    }
    oninit() {
        super.oninit();
        this.observe('visibilityLvl1 visibilityOE visibilityPos visibilityProductType visibilityLeverage visibilityDataSource DataSourceOpened', this.layoutTable);
        this.observe('instrument', this.onInstrumentChanged);
        this.observe('account', this.onAccountChanged);
        this.observe('orderType', this.onOrderTypeChanged);
        this.observe('instrument account quantity side tif productType userComment', this.onTradingDataChanged, {
            init: false
        });
        this.observe('reverseOrderButtons', this.updateButtonSides);
        this.on('placeOrder', this.placeOrder);
        this.updateSettings();
        this.observe('mirror', this.onMirrorChanged);
        this.onDisplayModeChanged = this.onDisplayModeChanged.bind(this);
        BaseSettings.displayModeChanged.Subscribe(this.onDisplayModeChanged, this);
        DataCache.OnUpdateAccount.Subscribe(this.onUpdateAccountData, this);
        DataCache.OnUpdateInstrument.Subscribe(this.onUpdateInstrument, this);
    }
    oncomplete() {
        super.oncomplete();
        this.applyTheme();
        this.deserializeDelayedData();
        this.localize();
    }
    dispose() {
        this.Unsubscribe(this.getInstrument());
        this.quickTableRactiveBID.quickTable.OnDrawCell.UnSubscribe(this.OnDrawCell, this);
        this.quickTableRactiveASK.quickTable.OnDrawCell.UnSubscribe(this.OnDrawCell, this);
        this.quickTableRactiveBID.quickTable.OnCellClicked.UnSubscribe(this.setPriceFromTable, this);
        this.quickTableRactiveASK.quickTable.OnCellClicked.UnSubscribe(this.setPriceFromTable, this);
        DataCache.OnUpdateAccount.UnSubscribe(this.onUpdateAccountData, this);
        DataCache.OnUpdateInstrument.UnSubscribe(this.onUpdateInstrument, this);
        BaseSettings.displayModeChanged.UnSubscribe(this.onDisplayModeChanged, this);
        super.dispose();
    }
    jbInit() {
        const PanelInfo = PanelItemsFactory.GetPanelItem(this.getType());
        if (!isNullOrUndefined(PanelInfo)) {
            const item = new PanelInfo();
            this.quickTableRactiveBID = this.createTable(this, item);
            this.quickTableRactiveASK = this.createTable(this, item);
        }
        const qtBid = this.quickTableRactiveBID.quickTable;
        const qtAsk = this.quickTableRactiveASK.quickTable;
        this.SetColumnsDefaultDisplayIndex(qtAsk);
        this.SetColumnsDefaultDisplayIndex(qtBid);
        qtBid.UpdateSortedColumns();
        qtAsk.UpdateSortedColumns();
        this.onMirrorChanged();
        qtBid.OnVisibleColumnChanged.Subscribe(this.visibleColumnChanged, this);
        qtAsk.OnVisibleColumnChanged.Subscribe(this.visibleColumnChanged, this);
        qtBid.OnVisibleColumnChanged.Subscribe(this.syncColumnsVisibility.bind(this, qtBid, qtAsk), this);
        qtAsk.OnVisibleColumnChanged.Subscribe(this.syncColumnsVisibility.bind(this, qtAsk, qtBid), this);
        qtBid.OnColumnResize.Subscribe(this.syncColumnsWidth.bind(this, qtBid, qtAsk), this);
        qtAsk.OnColumnResize.Subscribe(this.syncColumnsWidth.bind(this, qtAsk, qtBid), this);
        qtBid.ColumnDisplayIndexChanged.Subscribe(this.syncColumnsIndex.bind(this, qtBid, qtAsk), this);
        qtAsk.ColumnDisplayIndexChanged.Subscribe(this.syncColumnsIndex.bind(this, qtAsk, qtBid), this);
        qtBid.OnDrawCell.Subscribe(this.OnDrawCell, this);
        qtAsk.OnDrawCell.Subscribe(this.OnDrawCell, this);
        qtBid.OnCellClicked.Subscribe(this.setPriceFromTable, this);
        qtAsk.OnCellClicked.Subscribe(this.setPriceFromTable, this);
        this.OnResize.Subscribe(() => {
            this.layoutTable();
        }, this);
        qtBid.SetScrollHidden(true);
        qtAsk.scroll.OnValueChange.Subscribe(this.SynchronizeScroll, this);
        qtBid.scroll.OnValueChange.Subscribe(this.SynchronizeScroll, this);
        this.localize();
        this.layoutTable();
        this.visibleColumnChanged(qtBid.getVisibleColumn());
    }
    SynchronizeScroll(index) {
        const max = Math.max(this.quickTableRactiveBID.quickTable.scroll.scrollElementsCount, this.quickTableRactiveASK.quickTable.scroll.scrollElementsCount);
        this.quickTableRactiveBID.quickTable.scroll.scrollElementsCount =
            this.quickTableRactiveASK.quickTable.scroll.scrollElementsCount = max;
        this.quickTableRactiveBID.quickTable.scroll.moveScrollToElement(index);
        this.quickTableRactiveASK.quickTable.scroll.moveScrollToElement(index);
    }
    onOrderTypeChanged(newOrderType, oldOrderType) {
        if (isNullOrUndefined(newOrderType) || newOrderType === oldOrderType) {
            return;
        }
        this.isShowCommentSelector();
        const orderEdit = newOrderType.createOrderEditObject({
            dataCache: DataCache
            // TODO. Ugly.
            // http://tp.pfsoft.net/entity/76728
            // Remove https://tp.traderevolution.com/entity/82704
            // fullRangeForLimitPrice: true
        });
        this.setOrderEdit(orderEdit);
        this.localizeShortCaptions();
        orderEdit.updateParameters(new OrderEditUpdateData(null, this.getAllTradingDataDict()));
    }
    // TODO. Rename; Parameters' constants.
    onTradingDataChanged(newVal, oldVal, key) {
        const orderEdit = this.orderEdit;
        if (!isNullOrUndefined(orderEdit)) {
            const tradingDataDict = {};
            tradingDataDict[key] = newVal;
            orderEdit.updateParameters(new OrderEditUpdateData(null, tradingDataDict));
        }
        if (key === 'instrument') {
            this.symbolLink_Out(false, newVal);
        } // #115325 <- can't call it in onInstrumentChanged because in that case when linking is on there left fakeNonFixedInstrument in this.orderEdit.instrument
    }
    // TODO. Rename; Parameters' constants.
    getAllTradingDataDict() {
        const tradingDataDict = {};
        const instrument = this.get('instrument');
        if (!isNullOrUndefined(instrument)) {
            tradingDataDict.instrument = instrument;
        }
        const account = this.get('account');
        if (!isNullOrUndefined(account)) {
            tradingDataDict.account = account;
        }
        const quantity = this.get('quantity');
        if (!isNullOrUndefined(quantity)) {
            tradingDataDict.quantity = quantity;
        }
        const tif = this.get('tif');
        if (!isNullOrUndefined(tif)) {
            tradingDataDict.tif = tif;
        }
        const productType = this.get('productType');
        if (!isNullOrUndefined(productType)) {
            tradingDataDict.productType = productType;
        }
        const userComment = this.get('userComment');
        if (isValidString(userComment)) {
            tradingDataDict.userComment = userComment;
        }
        // TODO. UGLY. Default side.
        tradingDataDict.side = OperationType.Buy;
        return tradingDataDict;
    }
    placeOrder(sender, side) {
        if (TradingNumericErrorChecker.HasErrors(this)) {
            return;
        }
        const orderEdit = this.orderEdit;
        if (isNullOrUndefined(orderEdit))
            return;
        orderEdit.updateParameters(new OrderEditUpdateData(null, {
            side,
            placedFrom: PlacedFrom.WEB_MARKETDEPTH,
            leverageValue: this.get('leverageValue'),
            isAllowOTHTrading: this.get('isAllowOTHTrading')
        }));
        DataCache.FOrderExecutor.placeOrderPromise(orderEdit, null, null, this.focusWarningNumeric.bind(this))
            .catch(function () {
            const ex = new CustomErrorClass('MarketDepthPanel error', 'MarketDepthPanel.placeOrder.', 'placeOrder -> placeOrderPromise');
            ErrorInformationStorage.GetException(ex);
        })
            .finally(function () {
            // TODO. Ugly. Back to default side.
            orderEdit.updateParameters(new OrderEditUpdateData(null, { side: OperationType.Buy }));
        });
    }
    localize() {
        var _a, _b;
        super.localize();
        if (!isNullOrUndefined((_a = this.quickTableRactiveBID) === null || _a === void 0 ? void 0 : _a.quickTable)) {
            this.quickTableRactiveBID.quickTable.localize();
        }
        if (!isNullOrUndefined((_b = this.quickTableRactiveASK) === null || _b === void 0 ? void 0 : _b.quickTable)) {
            this.quickTableRactiveASK.quickTable.localize();
        }
        this.localizeShortCaptions();
        this.repopulatePanelContextMenu();
    }
    localizeShortCaptions() {
        var _a;
        const orderType = (_a = this.get('orderType')) === null || _a === void 0 ? void 0 : _a.id();
        const shortCaptions = [];
        const shortCaptionsTooltip = [];
        const addCaptionWithTooltip = (captionKey, tooltipKey) => {
            shortCaptions.push(Resources.getResource(captionKey));
            shortCaptionsTooltip.push(Resources.getResource(tooltipKey));
        };
        if (orderType === OrderType.Stop || orderType === OrderType.StopLimit || orderType === OrderType.OCO) {
            addCaptionWithTooltip('stopPrice.shortCaption', 'stopPrice.shortCaption.ToolTip');
        }
        if (orderType === OrderType.Limit ||
            orderType === OrderType.StopLimit ||
            orderType === OrderType.OCO ||
            orderType === OrderType.Care) {
            addCaptionWithTooltip('limitPrice.shortCaption', 'limitPrice.shortCaption.ToolTip');
        }
        if (orderType === OrderType.TrailingStop) {
            addCaptionWithTooltip('trailingStop.shortCaption', 'trailingStop.shortCaption.ToolTip');
        }
        void this.set({ shortCaptions, shortCaptionsTooltip });
    }
    repopulatePanelContextMenu() {
        var _a, _b;
        this.menuItems = this.createPanelMenuItems();
        this.menuTagDict = createTagDictionary(this.menuItems);
        if (!isNullOrUndefined((_a = this.quickTableRactiveBID) === null || _a === void 0 ? void 0 : _a.quickTable) &&
            !isNullOrUndefined((_b = this.quickTableRactiveASK) === null || _b === void 0 ? void 0 : _b.quickTable)) {
            this.quickTableRactiveBID.quickTable.setTableContextMenuItems(this.menuItems);
            this.quickTableRactiveASK.quickTable.setTableContextMenuItems(this.menuItems);
        }
    }
    createTable(me, items) {
        const myQuickTableRactive = new QuickTableRactive();
        me.addControl(myQuickTableRactive, true);
        const qt = myQuickTableRactive.quickTable;
        qt.allowGroupBy = false;
        qt.lockManualSorting = true;
        qt.InitializeDirect(items);
        qt.UpdateSortedColumns();
        return myQuickTableRactive;
    }
    coloringRowsMethod(coloringMethod, askRows, bidRows) {
        if (coloringMethod === ColoringMethod.ByPriceLVL) {
            this.coloringRowsMethodByPriceLVL(askRows);
            this.coloringRowsMethodByPriceLVL(bidRows);
        }
        else if (coloringMethod === ColoringMethod.RelativeToVolume) {
            this.coloringRowsMethodRelativeToVolume(askRows, false);
            this.coloringRowsMethodRelativeToVolume(bidRows, true);
        }
        else if (coloringMethod === ColoringMethod.SizeHistogram) {
            this.coloringRowsMethodSizeHistogram(askRows, false);
            this.coloringRowsMethodSizeHistogram(bidRows, true);
        }
    }
    coloringRowsMethodByPriceLVL(rows) {
        let depth = 0;
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (!row.isVisible())
                break;
            if (row !== row.table.hoveredRow) {
                row.BackColor = ThemeManager.colors.ByPriceBackColors[depth];
                row.ForeColor = ThemeManager.colors.ByPriceForeColors[depth];
            }
            row.cells[2].ForeColor = null;
            depth = (depth + 1) % 5;
        }
    }
    coloringRowsMethodRelativeToVolume(rows, isBidTable) {
        let maxVolume = 0;
        let minVolume = rows.length !== 0 ? rows[0].item.quoteSize : 0;
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const volume = row.item.quoteSize;
            maxVolume = Math.max(maxVolume, volume);
            minVolume = Math.min(minVolume, volume);
        }
        const deltaVolume = maxVolume - minVolume;
        const backColor = isBidTable
            ? ThemeManager.colors.RelVolMaxBackColorBid
            : ThemeManager.colors.RelVolMaxBackColorAsk;
        const foreColor = isBidTable
            ? ThemeManager.colors.RelVolMaxForeColorBid
            : ThemeManager.colors.RelVolMaxForeColorAsk;
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const volume = row.item.quoteSize;
            row.BackColor = MarketDepthPanel.RelativeToVolumeBackColoring(backColor, minVolume, deltaVolume, volume);
            row.ForeColor = foreColor;
            row.cells[2].ForeColor = null;
            if (row === row.table.hoveredRow) {
                row.table.selectedForeColor = isBidTable
                    ? ThemeManager.colors.RelVolHoverForeColorBid
                    : ThemeManager.colors.RelVolHoverForeColorAsk;
            }
        }
    }
    coloringRowsMethodSizeHistogram(rows, isBidTable) {
        let maxVolume = 0;
        const len = rows.length;
        for (let i = 0; i < len; i++) {
            const row = rows[i];
            if (!row.isVisible())
                break;
            maxVolume = Math.max(maxVolume, row.item.quoteSize);
        }
        for (let i = 0; i < len; i++) {
            const row = rows[i];
            if (!row.isVisible())
                break;
            row.BackColor = isBidTable
                ? ThemeManager.colors.SizeHistBackgroundBid
                : ThemeManager.colors.SizeHistBackgroundAsk;
            const coefVolume = row.item.quoteSize / maxVolume;
            row.cells[2].CustomDrawingHandlerParameters = { koef: coefVolume, isBidTable };
            row.ForeColor = ThemeManager.colors.SizeHistAllTextColor;
            row.cells[2].ForeColor = ThemeManager.colors.SizeHistTextColor;
        }
    }
    CustomDrawingHandler(context, cell, cellX, cellY, cellW, cellH) {
        const p = cell.CustomDrawingHandlerParameters;
        if (this.ColoringMethod !== ColoringMethod.SizeHistogram || isNullOrUndefined(p))
            return false;
        const w = p.koef * cellW;
        const x = Boolean(p.isBidTable) ? cellX + cellW - w : cellX;
        context.FillRect(new SolidBrush(ThemeManager.colors.SizeHistBackground), x, cellY, w, cellH);
        return true;
    }
    static RelativeToVolumeBackColoring(mostSaturatedColor, minVolume, deltaVolume, size) {
        const multiplier = (size - minVolume) / deltaVolume;
        let opacity = Number.isNaN(multiplier) ? 25 : 25 + Math.trunc(230 * multiplier);
        opacity = Math.max(0, Math.min(255, opacity));
        if (deltaVolume < 0)
            opacity = 255;
        const { r, g, b } = Color.getColorChannels(mostSaturatedColor);
        return `rgba(${r},${g},${b},${opacity / 255})`;
    }
    TickAsync() {
        var _a, _b;
        super.TickAsync();
        if ((_a = this.quickTableRactiveBID) === null || _a === void 0 ? void 0 : _a.quickTable.needRedraw) {
            this.quickTableRactiveBID.quickTable.Draw();
            this.quickTableRactiveBID.quickTable.needRedraw = false;
        }
        if ((_b = this.quickTableRactiveASK) === null || _b === void 0 ? void 0 : _b.quickTable.needRedraw) {
            this.quickTableRactiveASK.quickTable.Draw();
            this.quickTableRactiveASK.quickTable.needRedraw = false;
        }
    }
    onInstrumentChanged(instrument, lastInstrument) {
        if (isNullOrUndefined(instrument) || instrument === lastInstrument) {
            return;
        }
        if (Instrument.IsEqualInstrument(instrument, lastInstrument)) {
            return;
        }
        this.Unsubscribe(lastInstrument);
        this.UpdateTable();
        this.Subscribe();
        this.updateTradingAllowed();
        this.updateLeverageVisibility();
        // this.symbolLink_Out(false, instrument);  // #115325 <- need to call it in onTradingDataChanged otherwise when linking is on there left fakeNonFixedInstrument in this.orderEdit.instrument
        this.updatePanelHeader();
        this.isShowCommentSelector();
        this.updateIsVisibleOTHTrading();
    }
    updateTradingAllowed() {
        const account = this.get('account');
        const instrument = this.get('instrument');
        const ordertype = this.get('orderType');
        if (!isNullOrUndefined(instrument) && !isNullOrUndefined(account) && !isNullOrUndefined(ordertype)) {
            const isTradingAllowed = IsAllowed.IsTradingAllowed(account, instrument, ordertype.id());
            void this.set('tradingAllowed', isTradingAllowed.Allowed);
            void this.set('tradingForbiddenReason', isTradingAllowed.ReasonText);
        }
    }
    updateLeverageVisibility() {
        const instrument = this.get('instrument');
        const account = this.get('account');
        const productType = this.get('productType');
        let visible = false;
        if (!isNullOrUndefined(instrument)) {
            visible = instrument.isLeverageVisible(account, productType);
        }
        void this.set('visibilityLeverage', visible);
    }
    onUpdateInstrument(instrument) {
        const myInstrument = this.get('instrument');
        if (!isNullOrUndefined(myInstrument) && myInstrument === instrument) {
            this.updateTradingAllowed();
        }
    }
    onUpdateAccountData() {
        this.updateSettings(false);
    }
    onAccountChanged(account, lastAccount) {
        if (isNullOrUndefined(account)) {
            return;
        }
        this.accountLink_Out(false, account);
        this.updateTradingAllowed();
        this.updateLeverageVisibility();
        this.updateIsVisibleOTHTrading();
    }
    themeChange() {
        var _a, _b;
        super.themeChange();
        (_a = this.quickTableRactiveBID) === null || _a === void 0 ? void 0 : _a.themeChange();
        (_b = this.quickTableRactiveASK) === null || _b === void 0 ? void 0 : _b.themeChange();
        this.applyCustomDesignToQuickTables();
        this.setBackgroundColorForQuickTables(this.ColoringMethod);
    }
    // #region Link
    symbolLink_Out(newSubscriber, instrument) {
        if (isNullOrUndefined(instrument)) {
            const ins = this.get('instrument');
            if (isNullOrUndefined(ins))
                return;
            instrument = ins;
        }
        const color = this.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrument.GetInteriorID(), newSubscriber);
        }
    }
    symbolLink_In(symbolName) {
        const newInstr = DataCache.getInstrumentByName(symbolName);
        if (!isNullOrUndefined(newInstr)) {
            void this.set('instrument', newInstr);
        }
    }
    // #endregion Link
    createPanelMenuItems() {
        const onPanelMenuItemClicked = this.onPanelMenuItemClicked.bind(this);
        return [
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.Level2.menu.View',
                svgImage: 'View',
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.Level2.menu.View.Order Entry',
                        tag: ViewTag.OrderEntry,
                        checked: this.get('visibilityOE'),
                        canCheck: true,
                        event: onPanelMenuItemClicked
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.Level2.menu.View.Level1',
                        tag: ViewTag.Level1,
                        checked: this.get('visibilityLvl1'),
                        canCheck: true,
                        event: onPanelMenuItemClicked
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.Level2.menu.View.InstrInfo',
                        tag: ViewTag.PositionBar,
                        checked: this.get('visibilityPos'),
                        canCheck: true,
                        event: onPanelMenuItemClicked
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'property.MirrorShow',
                        tag: ViewTag.Mirror,
                        checked: this.get('mirror'),
                        canCheck: true,
                        event: onPanelMenuItemClicked
                    }
                ]
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'property.MarketDepth.ColoringMethod',
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'MarketDepthPanel.ColoringMethod.RelativeToVolume',
                        tag: ColoringMethod.RelativeToVolume,
                        checked: this.ColoringMethod === ColoringMethod.RelativeToVolume,
                        canCheck: true,
                        event: onPanelMenuItemClicked
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'MarketDepthPanel.ColoringMethod.ByPriceLVL',
                        tag: ColoringMethod.ByPriceLVL,
                        checked: this.ColoringMethod === ColoringMethod.ByPriceLVL,
                        canCheck: true,
                        event: onPanelMenuItemClicked
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'MarketDepthPanel.ColoringMethod.SizeHistogram',
                        tag: ColoringMethod.SizeHistogram,
                        checked: this.ColoringMethod === ColoringMethod.SizeHistogram,
                        canCheck: true,
                        event: onPanelMenuItemClicked
                    }
                ]
            }
        ];
    }
    onPanelMenuItemClicked(menuItem) {
        const { tag, checked } = menuItem;
        switch (tag) {
            case ViewTag.Mirror:
                void this.set('mirror', checked);
                break;
            case ViewTag.OrderEntry:
                void this.set('visibilityOE', checked);
                break;
            case ViewTag.Level1:
                void this.set('visibilityLvl1', checked);
                break;
            case ViewTag.PositionBar:
                void this.set('visibilityPos', checked);
                break;
            default:
                this.updateColoringMethod(tag);
        }
        this.UpdateTable();
    }
    setPriceFromTable(table, cellIndex, rowIndex) {
        if (cellIndex !== 1 && cellIndex !== 2) {
            return;
        }
        const item = table.rows[rowIndex].item;
        const price = item.getColumnValue(1);
        const orderEdit = this.orderEdit;
        if (isNullOrUndefined(orderEdit)) {
            return;
        }
        switch (orderEdit.getOrderTypeId()) {
            case OrderType.StopLimit:
                const stopLimitOrderEdit = orderEdit;
                stopLimitOrderEdit.setBasePrice(price, true);
                break;
            default:
                orderEdit.setBasePrice(price);
                break;
        }
    }
    // #region Table synchronization
    onMirrorChanged() {
        if (isNullOrUndefined(this.quickTableRactiveBID) || isNullOrUndefined(this.quickTableRactiveASK)) {
            return;
        }
        this.syncColumnsIndex(this.quickTableRactiveBID.quickTable, this.quickTableRactiveASK.quickTable);
    }
    syncColumnsVisibility(srcTable, outTable) {
        let changeOccured = false;
        const outColumns = outTable.columns;
        const srcColumns = srcTable.columns;
        const len = srcColumns.length;
        for (let i = 0; i < len; i++) {
            const srcColumn = srcColumns[i];
            const outColumn = outColumns[i];
            if (srcColumn.visible !== outColumn.visible) {
                outColumn.visible = srcColumn.visible;
                changeOccured = true;
            }
        }
        if (changeOccured) {
            outTable.UpdateSortedColumns();
            outTable.redraw();
            outTable.populateHeaderContextMenu(outColumns);
            outTable.OnVisibleColumnChanged.Raise(outTable.getVisibleColumn());
        }
    }
    SetColumnsDefaultDisplayIndex(table) {
        table.columns[0].displayedIndex = 9;
        table.columns[1].displayedIndex = 8;
        table.columns[2].displayedIndex = 7;
        table.columns[3].displayedIndex = 6;
        table.columns[4].displayedIndex = 5;
        table.columns[5].displayedIndex = 4;
        table.columns[7].displayedIndex = 3;
        table.columns[9].displayedIndex = 2;
        table.columns[10].displayedIndex = 1;
    }
    syncColumnsWidth(srcTable, outTable) {
        let changeOccured = false;
        const outColumns = outTable.columns;
        const srcColumns = srcTable.columns;
        const len = srcColumns.length;
        for (let i = 0; i < len; i++) {
            const srcColumn = srcColumns[i];
            const outColumn = outColumns[i];
            if (srcColumn.width !== outColumn.width) {
                outColumn.width = srcColumn.width;
                changeOccured = true;
            }
        }
        if (changeOccured) {
            outTable.redraw();
            outTable.OnColumnResize.Raise();
        }
    }
    syncColumnsIndex(srcTable, outTable) {
        if (isNullOrUndefined(srcTable) || isNullOrUndefined(outTable)) {
            return;
        }
        let changeOccured = false;
        const mirror = this.get('mirror');
        const outColumns = outTable.columns;
        const srcColumns = srcTable.columns;
        const len = srcColumns.length;
        if (mirror) {
            srcTable.CustomTextAlign = 'end';
            outTable.CustomTextAlign = 'start';
        }
        else {
            srcTable.CustomTextAlign = 'end';
            outTable.CustomTextAlign = 'end';
        }
        for (let i = 0; i < len; i++) {
            const srcColumn = srcColumns[i];
            const outColumn = outColumns[i];
            const srcDisplayedIdx = srcColumn.displayedIndex;
            const outDisplayedIdx = mirror ? len - srcDisplayedIdx - 1 : srcDisplayedIdx;
            if (outDisplayedIdx !== outColumn.displayedIndex) {
                outColumn.displayedIndex = outDisplayedIdx;
                changeOccured = true;
            }
        }
        if (changeOccured) {
            outTable.UpdateSortedColumns();
            outTable.redraw();
        }
    }
    // #endregion
    // TODO.
    repopulate() {
        var _a;
        super.repopulate();
        void this.set('account', this.get('account'));
        void this.set('instrument', this.get('instrument'));
        if (!isNullOrUndefined((_a = this.quickTableRactiveBID) === null || _a === void 0 ? void 0 : _a.quickTable)) {
            this.visibleColumnChanged(this.quickTableRactiveBID.quickTable.getVisibleColumn());
        }
    }
    // #region Subscribe Unsubscribe newQuote
    Subscribe() {
        var _a, _b;
        const value = this.get('instrument');
        if (isNullOrUndefined(value)) {
            return;
        }
        if (!isNullOrUndefined(value.DataCache)) {
            if (!isNullOrUndefined((_a = this.quickTableRactiveBID) === null || _a === void 0 ? void 0 : _a.quickTable)) {
                this.quickTableRactiveBID.quickTable.needRedrawBackground = true;
            }
            if (!isNullOrUndefined((_b = this.quickTableRactiveASK) === null || _b === void 0 ? void 0 : _b.quickTable)) {
                this.quickTableRactiveASK.quickTable.needRedrawBackground = true;
            }
            value.DataCache.FQuoteCache.addListener(value, this, HistoryType.QUOTE_TRADES);
            value.DataCache.FQuoteCache.addListener(value, this, HistoryType.QUOTE_LEVEL2);
            value.DataCache.FQuoteCache.addListener(value, this, HistoryType.QUOTE_LEVEL1);
            // this.newQuote(value.GetLastQuote(QuoteValid.Last));
        }
        value.RiskSettingsUpdated.Subscribe(this.onRiskSettingsUpdated, this);
    }
    Unsubscribe(lastInstrument) {
        if (isNullOrUndefined(lastInstrument)) {
            return;
        }
        lastInstrument.RiskSettingsUpdated.UnSubscribe(this.onRiskSettingsUpdated, this);
        if (!isNullOrUndefined(lastInstrument.DataCache)) {
            lastInstrument.DataCache.FQuoteCache.removeListener(lastInstrument, this, HistoryType.QUOTE_TRADES);
            lastInstrument.DataCache.FQuoteCache.removeListener(lastInstrument, this, HistoryType.QUOTE_LEVEL2);
            lastInstrument.DataCache.FQuoteCache.removeListener(lastInstrument, this, HistoryType.QUOTE_LEVEL1);
        }
    }
    newQuote(message) {
        if (isNullOrUndefined(message) && message.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR) {
            return;
        }
        this.UpdateTable();
    }
    // #endregion
    UpdateTable() {
        var _a, _b;
        const bidTable = (_a = this.quickTableRactiveBID) === null || _a === void 0 ? void 0 : _a.quickTable;
        const askTable = (_b = this.quickTableRactiveASK) === null || _b === void 0 ? void 0 : _b.quickTable;
        if (isNullOrUndefined(bidTable) || isNullOrUndefined(askTable))
            return;
        const instrument = this.getInstrument();
        if (isNullOrUndefined(instrument))
            return;
        const lvl2 = instrument.DataCache.FQuoteCache.GetLevel2Cash(instrument);
        if (isNullOrUndefined(lvl2))
            return;
        const spreadPlan = DataCache.GetSpreadPlan(this.get('account'));
        this.PopulateTable(bidTable, lvl2.GetBids(spreadPlan), true);
        this.PopulateTable(askTable, lvl2.GetAsks(spreadPlan), false);
        this.coloringRowsMethod(this.ColoringMethod, askTable.rowsArray, bidTable.rowsArray);
    }
    PopulateTable(QuickTableList, Level2CollectionLev2Collect, isBid) {
        const instrument = this.getInstrument();
        const account = this.get('account');
        const productType = this.get('productType');
        const items = Level2CollectionLev2Collect.GetSortedList(isBid);
        let aggregatedVolume = 0;
        let prevAggregatedVolume = 0;
        let avgPrice = 0;
        let lastAvgPrice = 0;
        let count = 0;
        const len = items.length;
        for (let i = 0; i < len; i++) {
            const item = items[i];
            const quoteMessage = item.Quote;
            const MMPD = quoteMessage.MMkey;
            // TODO
            const mdLotsMode = MDLotsMode.General;
            const roundPrecisionValue = 0;
            const roundPrecision = false;
            let row = QuickTableList.rows[count];
            let qItem;
            if (isNullOrUndefined(row)) {
                qItem = new Level2ItemQ(SessionSettings);
                qItem.id = i;
            }
            else {
                qItem = row.item;
            }
            // Подсчет Aggregated Volume
            if (quoteMessage.QuoteType === Leve2QuoteType.None && !isNaN(quoteMessage.Price)) {
                aggregatedVolume += quoteMessage.Size;
                // Подсчет VMA Price
                avgPrice =
                    (lastAvgPrice * prevAggregatedVolume + quoteMessage.Price * quoteMessage.Size) /
                        (prevAggregatedVolume + quoteMessage.Size);
            }
            item.AggregatedVolume = aggregatedVolume;
            item.VWAPrice = avgPrice;
            // запоминаем значения
            lastAvgPrice = avgPrice;
            prevAggregatedVolume = aggregatedVolume;
            qItem.SetAccount(account);
            qItem.SetProductType(productType);
            qItem.SetQuoteMessage(quoteMessage);
            qItem.count = count;
            qItem.VWAP = item.VWAPrice;
            qItem.SetInstrument(instrument);
            qItem.MMPD = MMPD;
            qItem.aggregatedVolume = item.AggregatedVolume;
            qItem.mdLotsMode = mdLotsMode;
            qItem.RoundedPrecisionValue = roundPrecisionValue;
            qItem.RoundedPrecision = roundPrecision;
            qItem.ItemId = count;
            if (isNullOrUndefined(row)) {
                row = QuickTableList.AddItem(qItem);
                row.cells[2].CustomDrawingHandler = this.CustomDrawingHandler.bind(this);
            }
            else {
                row.updateItem(qItem);
            }
            qItem.Visible = true;
            if (!isNullOrUndefined(row)) {
                row.visible = true;
            }
            count++;
        }
        if (!isNullOrUndefined(QuickTableList.scroll)) {
            QuickTableList.scroll.setScrollElementsCount(count);
        }
        // Скрываем лишнее
        for (let i = count; i < QuickTableList.rowsArray.length; i++) {
            QuickTableList.rowsArray[i].item.Visible = false;
            QuickTableList.rowsArray[i].visible = false;
            // QuickTable_list.rowsArray[i].SkipAlertFilter = true;
        }
        QuickTableList.redraw();
    }
    getInstrument() {
        return this.get('instrument');
    }
    // #region ICaller
    callBack(properties) {
        super.callBack(properties);
        void this.set('instrument', this.getCallBackInstrument(properties, 'symbol'));
        let dp = DynProperty.getPropertyByName(properties, 'account');
        if (DynProperty.isCorrect(dp)) {
            void this.set('account', SessionSettings.getDefValueFromObj(DataCache.Accounts[dp.value], DataCache.Accounts));
        }
        dp = DynProperty.getPropertyByName(properties, 'colorMetod');
        if (!isNullOrUndefined(dp)) {
            this.updateColoringMethod(dp.value);
        }
        for (const propName of BOOLEAN_PROPERTIES) {
            dp = DynProperty.getPropertyByName(properties, propName);
            if (!isNullOrUndefined(dp)) {
                void this.set(propName, dp.value);
            }
        }
        const level1Panel = this.findComponent('terceraLevel1Panel');
        level1Panel.callBack(properties);
    }
    Properties() {
        const properties = super.Properties();
        const ins = this.get('instrument');
        if (!isNullOrUndefined(ins)) {
            properties.push(new DynProperty('symbol', ins.GetInteriorID(), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        const acc = this.get('account');
        if (!isNullOrUndefined(acc)) {
            properties.push(new DynProperty('account', acc.AcctNumber, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        properties.push(new DynProperty('colorMetod', this.ColoringMethod, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP));
        for (const propName of BOOLEAN_PROPERTIES) {
            properties.push(new DynProperty(propName, this.get(propName), DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        }
        const level1Panel = this.findComponent('terceraLevel1Panel');
        properties.push(...level1Panel.properties());
        return properties;
    }
    // #endregion
    onRiskSettingsUpdated() {
        this.updateSettings(true);
    }
    updateSettings(needRepopulate = false) {
        void this.set('reverseOrderButtons', WDSettings.reverseButtonsOrder);
        this.UpdateTable();
        if (needRepopulate) {
            this.repopulate(); // обновляет comboBox-ы (OrderType и Tif) у MarketDepthPanel и AdvancedOrderEntry
        }
        this.updateTradingAllowed();
    }
    // #region ISerializable
    serialize() {
        const serializedData = super.serialize();
        serializedData.table = {
            bid: this.quickTableRactiveBID.quickTable.serialize(),
            ask: this.quickTableRactiveASK.quickTable.serialize()
        };
        return serializedData;
    }
    deserialize(serialized) {
        super.deserialize(serialized);
        if (!this.completed) {
            return;
        }
        this.quickTableRactiveBID.quickTable.deserialize(serialized.table.bid);
        this.quickTableRactiveASK.quickTable.deserialize(serialized.table.ask);
    }
    deserializeOldScheme(serialized) {
        super.deserializeOldScheme(serialized);
        if (!this.completed) {
            return;
        }
        this.quickTableRactiveBID.quickTable.deserializeOldScheme(serialized.xmlSettingsTemplate);
        this.quickTableRactiveASK.quickTable.deserializeOldScheme(serialized.xmlSettingsTemplate);
        this.onMirrorChanged();
    }
    // #region Hotkeys
    HotkeyPressed(hotkey) {
        const isPressed = super.HotkeyPressed(hotkey);
        if (isPressed) {
            return true;
        }
        const account = this.get('account');
        const instrument = this.get('instrument');
        const hotkeyAction = HotkeysManager.GetActionByHotkeyForPanel(hotkey, this.getType());
        switch (hotkeyAction) {
            case MarketDepthHotkeysEnum.CancelLastOrderOnSelectedInstrument:
                return this.ProcessHotkeyCancelOrder(account, instrument, OrderCancelActionHotkeysEnum.LastOrder, PlacedFrom.WEB_MARKETDEPTH);
            case MarketDepthHotkeysEnum.CancelAllActiveOrdersOnSelectedInstrument:
                return this.ProcessHotkeyCancelOrder(account, instrument, OrderCancelActionHotkeysEnum.AllOrders, PlacedFrom.WEB_MARKETDEPTH);
            case MarketDepthHotkeysEnum.CancelBuyOrderClosestToTheLastPrice:
                return this.ProcessHotkeyCancelOrder(account, instrument, OrderCancelActionHotkeysEnum.BuyOrder, PlacedFrom.WEB_MARKETDEPTH);
            case MarketDepthHotkeysEnum.CancelSellOrderClosestToTheLastPrice:
                return this.ProcessHotkeyCancelOrder(account, instrument, OrderCancelActionHotkeysEnum.SellOrder, PlacedFrom.WEB_MARKETDEPTH);
            case MarketDepthHotkeysEnum.PlaceBuyOrder:
                this.placeOrder(null, OperationType.Buy);
                return true;
            case MarketDepthHotkeysEnum.PlaceSellOrder:
                this.placeOrder(null, OperationType.Sell);
                return true;
            case MarketDepthHotkeysEnum.SellAsk:
                this.addBuyBidOrSellAskLimit(OperationType.Sell);
                return true;
            case MarketDepthHotkeysEnum.BuyBid:
                this.addBuyBidOrSellAskLimit(OperationType.Buy);
                return true;
        }
        return false;
    }
    addBuyBidOrSellAskLimit(side) {
        const account = this.get('account');
        const instrument = this.get('instrument');
        if (isNullOrUndefined(account) || isNullOrUndefined(instrument === null || instrument === void 0 ? void 0 : instrument.LastQuote)) {
            return;
        }
        const sp = DataCache.GetSpreadPlan(account);
        const price = side === OperationType.Buy
            ? instrument.LastQuote.BidSpread_SP_Ins(sp, instrument)
            : instrument.LastQuote.AskSpread_SP_Ins(sp, instrument);
        if (!isNaN(price)) {
            this.hotkeyActionAddOrder(price, side);
        }
    }
    hotkeyActionAddOrder(price, side) {
        const orderEdit = new LimitOrderType().createOrderEditObject({ dataCache: DataCache });
        const updateData = new OrderEditUpdateData(null, this.getAllTradingDataDict());
        updateData.tradingDataDict.side = side;
        orderEdit.updateParameters(updateData);
        orderEdit.setBasePrice(price);
        void DataCache.FOrderExecutor.placeOrderPromise(orderEdit, null, null, this.focusWarningNumeric.bind(this))
            .catch(function () {
            const ex = new CustomErrorClass('MarketDepthPanel error', 'MarketDepthPanel.placeOrder.', 'placeOrder -> placeOrderPromise');
            ErrorInformationStorage.GetException(ex);
        })
            .finally(function () {
            orderEdit.dispose();
        });
    }
    applyCustomDesignToQuickTables() {
        var _a, _b;
        this.applyCustomDesignToQuickTable((_a = this.quickTableRactiveBID) === null || _a === void 0 ? void 0 : _a.quickTable);
        this.applyCustomDesignToQuickTable((_b = this.quickTableRactiveASK) === null || _b === void 0 ? void 0 : _b.quickTable);
    }
    applyCustomDesignToQuickTable(qt) {
        if (!isNullOrUndefined(qt)) {
            qt.rowHeight = MARKET_DEPTH_ROW_HEIGHT;
            qt.fontSize = 12;
            qt.isRowSeparated = true;
            qt.borderWidth = 0;
        }
    }
    OnDrawCell(ctx, e) {
        var _a;
        if (this.ColoringMethod !== ColoringMethod.SizeHistogram ||
            e.columnIndex !== 2 ||
            isNullOrUndefined((_a = e.cell) === null || _a === void 0 ? void 0 : _a.CustomDrawingHandlerParameters)) {
            return;
        }
        const { cell, x, y, width, height, table, parentRowIsHovered } = e;
        const { koef, isBidTable } = cell.CustomDrawingHandlerParameters;
        const textBrush = new SolidBrush(ThemeManager.colors.SizeHistTextColor);
        const rectBrush = parentRowIsHovered
            ? new SolidBrush(ThemeManager.colors.SizeHistHoverBackground)
            : new SolidBrush(ThemeManager.colors.SizeHistBackground);
        const w = koef * width;
        const barX = Boolean(isBidTable) ? x + width - w : x;
        ctx.FillRect(rectBrush, barX, y, w, height);
        const xPadding = 5;
        const yPadding = (height - QuickTableUtils.GetTextHeight(ctx)) / 2;
        QuickTableUtils.DrawTextInRect(ctx, cell.formattedValue, table.tableFont, textBrush, x, y, width, height, xPadding, yPadding, true);
        e.handled = true;
    }
    setBackgroundColorForQuickTables(cM) {
        const bid = this.quickTableRactiveBID.quickTable;
        const ask = this.quickTableRactiveASK.quickTable;
        if (isNullOrUndefined(bid) || isNullOrUndefined(ask))
            return;
        ask.BackColor = bid.BackColor = ThemeManager.colors.AttachedBackground;
        ask.ForeColor = bid.ForeColor = ThemeManager.colors.TableSelectionForeColor;
        switch (cM) {
            case ColoringMethod.ByPriceLVL:
                ask.hoveredForeColor = bid.hoveredForeColor = ThemeManager.colors.ByPriceHoverForeground;
                ask.hoveredBackColor = bid.hoveredBackColor = ThemeManager.colors.ByPriceHoverBackground;
                ask.selectedBackColor = bid.selectedBackColor = ThemeManager.colors.ByPriceSelectedBackground;
                break;
            case ColoringMethod.RelativeToVolume:
                ask.hoveredForeColor = ThemeManager.colors.RelVolHoverForeColorAsk;
                bid.hoveredForeColor = ThemeManager.colors.RelVolHoverForeColorBid;
                bid.selectedBackColor = ThemeManager.colors.RelVolSelectedBackgroundBid;
                ask.selectedBackColor = ThemeManager.colors.RelVolSelectedBackgroundAsk;
                bid.hoveredBackColor = ThemeManager.colors.RelVolHoverBackgroundBid;
                ask.hoveredBackColor = ThemeManager.colors.RelVolHoverBackgroundAsk;
                break;
            case ColoringMethod.SizeHistogram:
                ask.hoveredForeColor = bid.hoveredForeColor = ThemeManager.colors.ByPriceHoverForeground;
                bid.selectedBackColor = ThemeManager.colors.ByPriceSelectedBackgroundBid;
                ask.selectedBackColor = ThemeManager.colors.ByPriceSelectedBackgroundAsk;
                bid.hoveredBackColor = ThemeManager.colors.ByPriceHoverBackgroundBid;
                ask.hoveredBackColor = ThemeManager.colors.ByPriceHoverBackgroundAsk;
                break;
        }
    }
    updateColoringMethod(coloringMethod) {
        this.ColoringMethod = coloringMethod;
        this.repopulatePanelContextMenu();
        this.setBackgroundColorForQuickTables(this.ColoringMethod);
    }
    updateButtonSides(reverseOrderButtons) {
        void this.set({
            leftBtnSide: reverseOrderButtons ? OperationType.Sell : OperationType.Buy,
            rightBtnSide: reverseOrderButtons ? OperationType.Buy : OperationType.Sell
        });
    }
    onDisplayModeChanged() {
        this.layoutTable();
    }
}
var ViewTag;
(function (ViewTag) {
    ViewTag["OrderEntry"] = "oe";
    ViewTag["Level1"] = "lvl1";
    ViewTag["PositionBar"] = "pos";
    ViewTag["Mirror"] = "mirror";
})(ViewTag || (ViewTag = {}));
var ColoringMethod;
(function (ColoringMethod) {
    ColoringMethod[ColoringMethod["ByPriceLVL"] = 0] = "ByPriceLVL";
    ColoringMethod[ColoringMethod["RelativeToVolume"] = 1] = "RelativeToVolume";
    ColoringMethod[ColoringMethod["StepToMaxVolume"] = 2] = "StepToMaxVolume";
    ColoringMethod[ColoringMethod["ByUpdateTime"] = 3] = "ByUpdateTime";
    ColoringMethod[ColoringMethod["SizeHistogram"] = 4] = "SizeHistogram";
    ColoringMethod[ColoringMethod["None"] = 5] = "None";
})(ColoringMethod || (ColoringMethod = {}));
OrderEditViewBase.extendWith(MarketDepthPanel, {
    data() {
        return {
            width: 760,
            height: 400,
            overnightExecutionTextKey: 'panel.newOrderEntry.OvernightExecution.Short',
            overnightExecutionTooltipKeyActive: 'panel.newOrderEntry.OvernightExecution.Info.NoSLTP.Active',
            overnightExecutionTooltipKeyInActive: 'panel.newOrderEntry.OvernightExecution.Info.NoSLTP.InActive',
            isAccountLinkShow: true,
            isSymbolLinkShow: true,
            instrument: null,
            account: null,
            quantity: null, // QuantityObject
            tif: null, // TIF object
            productType: null,
            orderType: null,
            enableComment: false,
            // TODO. UGLY.
            defaultQuantity: null,
            mirror: true,
            canFilterByAccount: false,
            reverseOrderButtons: false,
            leftBtnSide: OperationType.Buy,
            rightBtnSide: OperationType.Sell,
            shortCaptions: [], //  array of 0-2 strings from { 'LMT:', 'STP:', 'Offset:' } captions set
            visibilityLvl1: true,
            visibilityOE: true,
            visibilityPos: false,
            visibilityProductType: false,
            visibilityLeverage: false,
            visibilityDataSource: true,
            DataSourceOpened: false,
            level1Types: [
                Level1ItemType.Last,
                Level1ItemType.Open,
                Level1ItemType.ChangePercent,
                Level1ItemType.High,
                Level1ItemType.Volume,
                Level1ItemType.Low,
                Level1ItemType.IndicativeAuctionPrice,
                Level1ItemType.SettlementPrice,
                Level1ItemType.PrevSettlementPrice,
                Level1ItemType.VWAP,
                Level1ItemType.ReferencePrice,
                Level1ItemType.Ask,
                Level1ItemType.Spread,
                Level1ItemType.LastTime,
                Level1ItemType.ETB,
                Level1ItemType.PrevClose,
                Level1ItemType.Change,
                Level1ItemType.Bid,
                Level1ItemType.BidSize,
                Level1ItemType.AskSize,
                Level1ItemType.LastSize,
                Level1ItemType.OpenInterest,
                Level1ItemType.BidSource,
                Level1ItemType.AskSource,
                Level1ItemType.LastSource,
                Level1ItemType.Funding,
                Level1ItemType.Countdown,
                Level1ItemType.AuctionStart,
                Level1ItemType.AuctionEnd,
                Level1ItemType.LastUpdateTime,
                Level1ItemType.CurrentSession,
                Level1ItemType.TradingStatus,
                Level1ItemType.TradedValue,
                Level1ItemType.OffExchangeVolume,
                Level1ItemType.OffExchangeValue,
                Level1ItemType.NormalMarketSize,
                Level1ItemType.LimitHigh,
                Level1ItemType.LimitLow,
                Level1ItemType.RemainingQty,
                Level1ItemType.RemainingQtySide,
                Level1ItemType.TotalBuyQty,
                Level1ItemType.TotalSellQty,
                Level1ItemType.AvgTradedPrice,
                Level1ItemType.FiftyTwoWeekHighPrice,
                Level1ItemType.FiftyTwoWeekLowPrice,
                Level1ItemType.NSEValue,
                Level1ItemType.DPR,
                Level1ItemType.LastTradedTime
            ]
        };
    },
    partials: {
        bodyPartial: MarketDepthOETemplate
    }
});
